import { EditorView } from "@codemirror/view";

export const tableGeneratorTheme = EditorView.baseTheme({
    "&dark .table-generator": {
        "--table-generator-active-border-color": "#ccc",
        "--table-generator-divider-color": "rgba(125,125,125,0.3)",
        "--table-generator-focus-border-color": "#5d7498",
        "--table-generator-inactive-border-color": "#888",
        "--table-generator-selected-background-color": "#ffffff2a",
        "--table-generator-selector-background-color": "#777",
        "--table-generator-selector-hover-color": "#3265b2",
        "--table-generator-toolbar-background": "#2c3645",
        "--table-generator-toolbar-button-active-background": "rgba(125, 125, 125, 0.4)",
        "--table-generator-toolbar-button-color": "#fff",
        "--table-generator-toolbar-button-hover-background": "rgba(125, 125, 125, 0.2)",
        "--table-generator-toolbar-shadow-color": "#1e253029",
        "--table-generator-error-background": "#2c3645",
        "--table-generator-error-color": "#fff",
        "--table-generator-error-border-color": "#677283",
    },
    "&light .table-generator": {
        "--table-generator-active-border-color": "#666",
        "--table-generator-divider-color": "var(--neutral-20, #e4e9ef)",
        "--table-generator-focus-border-color": "#97b6e5",
        "--table-generator-inactive-border-color": "#dedede",
        "--table-generator-selected-background-color": "var(--blue-10, #eef5ff)",
        "--table-generator-selector-background-color": "var(--neutral-30, #cdd6e1)",
        "--table-generator-selector-hover-color": "#3265b2",
        "--table-generator-toolbar-background": "#fff",
        "--table-generator-toolbar-button-active-background": "rgba(47, 58, 76, 0.16)",
        "--table-generator-toolbar-button-color": "var(--neutral-70, #4c5f73)",
        "--table-generator-toolbar-button-hover-background": "rgba(47, 58, 76, 0.08)",
        "--table-generator-toolbar-shadow-color": "#1e253029",
        "--table-generator-error-background": "#f1f4f9",
        "--table-generator-error-color": "black",
        "--table-generator-error-border-color": "#c3d0e3",
    },
    ".table-generator": {
        position: "relative",
        "--table-generator-inactive-border-width": "1px",
        "--table-generator-active-border-width": "1px",
        "--table-generator-selector-handle-buffer": "12px",
        "--table-generator-focus-border-width": "2px",
        "--table-generator-focus-negative-border-width": "-2px",
    },
    ".table-generator-cell.selected": {
        backgroundColor: "var(--table-generator-selected-background-color)",
    },
    ".table-generator-cell:focus-visible": {
        outline: "2px dotted var(--table-generator-focus-border-color)",
    },
    ".table-generator-cell": {
        border: "var(--table-generator-inactive-border-width) dashed var(--table-generator-inactive-border-color)",
        minWidth: "40px",
        height: "30px",
        boxShadow:
            "var(--shadow-top, 0 0 0 transparent), var(--shadow-bottom, 0 0 0 transparent), var(--shadow-left, 0 0 0 transparent), var(--shadow-right, 0 0 0 transparent)",
        "&.selection-edge-top": {
            "--shadow-top":
                "0 var(--table-generator-focus-negative-border-width) 0 var(--table-generator-focus-border-color)",
        },
        "&.selection-edge-bottom": {
            "--shadow-bottom":
                "0 var(--table-generator-focus-border-width) 0 var(--table-generator-focus-border-color)",
        },
        "&.selection-edge-left": {
            "--shadow-left":
                "var(--table-generator-focus-negative-border-width) 0 0 var(--table-generator-focus-border-color)",
        },
        "&.selection-edge-right": {
            "--shadow-right": "var(--table-generator-focus-border-width) 0 0 var(--table-generator-focus-border-color)",
        },
        "&.table-generator-cell-border-left": {
            borderLeftStyle: "solid",
            borderLeftColor: "var(--table-generator-active-border-color)",
            borderLeftWidth: "var(--table-generator-active-border-width)",
        },
        "&.table-generator-cell-border-right": {
            borderRightStyle: "solid",
            borderRightColor: "var(--table-generator-active-border-color)",
            borderRightWidth: "var(--table-generator-active-border-width)",
        },
        "&.table-generator-row-border-top": {
            borderTopStyle: "solid",
            borderTopColor: "var(--table-generator-active-border-color)",
            borderTopWidth: "var(--table-generator-active-border-width)",
        },
        "&.table-generator-row-border-bottom": {
            borderBottomStyle: "solid",
            borderBottomColor: "var(--table-generator-active-border-color)",
            borderBottomWidth: "var(--table-generator-active-border-width)",
        },
        "& .table-generator-cell-render": {
            overflowX: "auto",
            overflowY: "hidden",
            width: "100%",
        },
    },
    ".table-generator-table": {
        tableLayout: "fixed",
        width: "95%",
        maxWidth: "95%",
        margin: "0 auto",
        cursor: "default",
        "& td": {
            verticalAlign: "top",
            "&:not(.editing)": {
                padding: "0 0.25em",
            },
            "&.alignment-left": {
                textAlign: "left",
            },
            "&.alignment-right": {
                textAlign: "right",
            },
            "&.alignment-center": {
                textAlign: "center",
            },
            "&.alignment-paragraph": {
                textAlign: "justify",
            },
        },
        "& .table-generator-selector-cell": {
            padding: "0",
            border: "none !important",
            position: "relative",
            cursor: "pointer",
            "&.row-selector": {
                width: "calc(var(--table-generator-selector-handle-buffer) + 8px)",
                "&::after": {
                    width: "4px",
                    bottom: "4px",
                    height: "calc(100% - 8px)",
                },
            },
            "&.column-selector": {
                height: "calc(var(--table-generator-selector-handle-buffer) + 8px)",
                "&::after": {
                    width: "calc(100% - 8px)",
                    height: "4px",
                    right: "4px",
                },
            },
            "&::after": {
                content: '""',
                display: "block",
                position: "absolute",
                bottom: "8px",
                right: "8px",
                width: "calc(100% - 8px)",
                height: "calc(100% - 8px)",
                backgroundColor: "var(--table-generator-selector-background-color)",
                borderRadius: "4px",
            },
            "&:hover::after": {
                backgroundColor: "var(--table-generator-selector-hover-color)",
            },
            "&.fully-selected::after": {
                backgroundColor: "var(--table-generator-selector-hover-color)",
            },
        },
    },
    ".ol-cm-environment-table.table-generator-error-container, .ol-cm-environment-table.ol-cm-tabular": {
        marginTop: "0.75rem",
        marginBottom: "0.75rem",
    },
});
